import { Component, OnInit, Output, EventEmitter, Input, OnDestroy, inject, input, signal, effect } from '@angular/core'; // Removed signal, Inject

import { Capacitor } from '@capacitor/core';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';

import { Subject, Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';

import { TOAST_ALERTS_TOKEN } from '@dataclouder/ngx-core';

import { MicRecordingService } from '../../services/mic-recording.service'; // Import MicStatus from service
import { MicStateService } from './mic-state.service'; // MicStatus is now from mic-recording.service
import { SpeechRecognizerService } from '../../services/speech-recognizer.service';
import { KnobModule } from 'primeng/knob';
import { FormsModule } from '@angular/forms';
import { MicStatus } from '../../models/mic.models';

export interface MicSettings {
  micMode?: 'recognition' | 'recording';
  recordMode?: 'press' | 'silence';
  lang: string;
}

@Component({
  selector: 'dc-mic',
  templateUrl: './mic.component.html',
  styleUrls: ['./mic.component.scss'],
  standalone: true,
  imports: [ButtonModule, KnobModule, FormsModule],
})
export class DCMicComponent implements OnInit, OnDestroy {
  // Inputs
  @Input() targetOrBase = 'target';
  @Input() micSettings: MicSettings = { lang: 'en', micMode: 'recognition' };
  // Outputs
  @Output() onInterpretedText = new EventEmitter<string>();
  @Output() onFinishedRecognition = new EventEmitter<boolean>();
  @Output() onFinished = new EventEmitter<any>();

  // Signal States
  public isDone = input(false);
  public noiseLevel = signal(0);

  // Subscriptions
  private destroy$ = new Subject<void>();
  private recognitionSubscription?: Subscription;

  // Services
  private toastService = inject(TOAST_ALERTS_TOKEN);
  public micRecordingService = inject(MicRecordingService);
  private speechRecognizerService = inject(SpeechRecognizerService);
  public micStateService = inject(MicStateService); // Inject MicStateService

  constructor() {
    effect(() => {
      this.noiseLevel.set(Math.round(this.micRecordingService.rms() * 1200));
    });

    // Effect for service status influencing component's state service
    effect(() => {
      const status = this.micRecordingService.status();
      if (this.micSettings.micMode === 'recording') {
        this.micStateService.changeMicStatus(status); // status is already MicStatus type
      }
    });

    // New effect for the recorded audio blob
    effect(() => {
      const audioBlob = this.micRecordingService.recordedAudioBlob();
      if (audioBlob) {
        console.log('DCMicComponent: Detected new audioBlob via signal', audioBlob);
        this.onFinished.emit(audioBlob);
      }
    });
  }

  ngOnInit() {
    console.log('micSettings', this.micSettings);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.recognitionSubscription && !this.recognitionSubscription.closed) {
      this.recognitionSubscription.unsubscribe();
    }
  }

  public async hasMicPermission() {
    if (Capacitor.isNativePlatform()) {
      const permission = await SpeechRecognition.checkPermissions();
      return ['granted', 'prompt'].includes(permission.speechRecognition) ? true : false;
    } else {
      const permission = await navigator.permissions.query({ name: 'microphone' as any });
      console.log(permission);
      return ['granted', 'prompt'].includes(permission.state) ? true : false;
    }
  }

  public stopMic(): void {
    if (this.recognitionSubscription && !this.recognitionSubscription.closed) {
      this.recognitionSubscription.unsubscribe();
    }
    this.recognitionSubscription = undefined; // Clear the subscription

    if (Capacitor.isNativePlatform()) {
      this.speechRecognizerService.stopCapacitorRecognition();
    } else {
      this.speechRecognizerService.stopWebRecognition();
    }
  }

  public async initStopMicState() {
    if (this.micSettings.recordMode === 'press') {
      console.log('Mic in "press" mode. Use press and hold to record.');
      return;
    }

    const canUseMic = await this.hasMicPermission();

    if (!canUseMic) {
      this.toastService.warn({ title: 'No se puede utilizar el microphono', subtitle: 'Por favor concede permisos para utilizar el microfono' });
      return;
    }

    // Just for fun will use both modes
    this.toogleRecording();
  }

  public async toogleRecording() {
    if (this.micRecordingService.status() === MicStatus.Inactive) {
      this.micRecordingService.startContinuousRecording();
    } else {
      console.log('Forcing stop recording. Current service status:', this.micRecordingService.status());
      this.micRecordingService.stopMicrophone();
    }
  }
}
